import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { translations, SUPPORTED_LANGUAGES } from '../i18n';

const LanguageContext = createContext(undefined);

const STORAGE_KEY = 'inventory_app_language';

const readStoredLanguage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return SUPPORTED_LANGUAGES.includes(stored) ? stored : 'en';
  } catch {
    return 'en';
  }
};

const resolveValue = (lang, key) => {
  const parts = key.split('.');
  let value = translations[lang];
  for (const part of parts) {
    value = value?.[part];
  }
  return value;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStoredLanguage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // localStorage unavailable (e.g. private mode) - language just won't persist
    }
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      setLanguageState(lang);
    }
  }, []);

  // Usage: t('nav.dashboard') or t('login.footer', { year: 2026 })
  const t = useCallback(
    (key, vars) => {
      let value = resolveValue(language, key);
      if (value === undefined) {
        value = resolveValue('en', key) ?? key;
      }
      if (typeof value === 'string' && vars) {
        return Object.keys(vars).reduce(
          (str, varKey) =>
            str.replace(new RegExp(`{{${varKey}}}`, 'g'), vars[varKey]),
          value
        );
      }
      return value;
    },
    [language]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
};
